/**
 * usePendingApprovals Hook
 * AIDLC Administrative Backend
 *
 * One source for the approval cards on the Agent Dashboard and in Chat:
 *   - loads the waiting approvals through the authenticated apiClient (APPROVAL-TOKEN-1, BL-beb6a8cc)
 *   - follows 'approval:request' / 'approval:response' socket pushes
 *   - removes a card only after the server accepted the answer; a refused or failed answer keeps the
 *     card and reports a plain message for it
 *   - reports a failed load instead of hiding it (MAYA-AW-02, BL-86788e85)
 *
 * Only Admins and Operators may answer (the server's APPROVER_ROLES). For anyone else nothing is asked
 * for, so a Viewer never sees an error for a list they are not meant to have.
 */

import { useState, useEffect, useCallback } from 'react';
import { useBridge } from '../components/layout/MainLayout';
import { useAuth } from './useAuth';
import {
  getPendingApprovals,
  respondToApproval,
  approvalAnswerError,
  APPROVALS_LOAD_ERROR,
  type ApprovalRequest,
  type ApprovalDecision,
} from '../services/approvalService';

interface UsePendingApprovalsResult {
  approvals: ApprovalRequest[];
  /** Plain message when the list could not be loaded, else null. */
  loadError: string | null;
  /** The request whose answer is on its way, else null. */
  respondingTo: string | null;
  /** Plain message per request id whose last answer was not accepted. */
  answerErrors: Record<string, string>;
  /** Sends an answer; resolves true only when the server accepted it. */
  respond: (requestId: string, decision: ApprovalDecision, message: string) => Promise<boolean>;
}

function withoutKey(map: Record<string, string>, key: string): Record<string, string> {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
}

export function usePendingApprovals(): UsePendingApprovalsResult {
  const { socket } = useBridge();
  const { isAdmin, isOperator } = useAuth();
  const canAnswer = isAdmin || isOperator;
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});

  const removeApproval = useCallback((requestId: string) => {
    setApprovals((prev) => prev.filter((a) => a.id !== requestId));
    setAnswerErrors((prev) => withoutKey(prev, requestId));
  }, []);

  // Initial list (independent of the socket)
  useEffect(() => {
    if (!canAnswer) return;
    let cancelled = false;
    getPendingApprovals()
      .then((list) => {
        if (cancelled) return;
        setLoadError(null);
        // Merge: a socket push may have arrived before the list did
        setApprovals((prev) => [...list, ...prev.filter((p) => !list.some((a) => a.id === p.id))]);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[usePendingApprovals] load failed', err);
        setLoadError(APPROVALS_LOAD_ERROR);
      });
    return () => { cancelled = true; };
  }, [canAnswer]);

  // Live pushes: a new request, or an answer accepted from any tab or path
  useEffect(() => {
    if (!socket) return;
    const handleRequest = (data: ApprovalRequest) => {
      setApprovals((prev) => (prev.some((a) => a.id === data.id) ? prev : [...prev, data]));
    };
    const handleResponse = (data: { request?: { id?: string } }) => {
      const id = data?.request?.id;
      if (id) removeApproval(id);
    };
    socket.on('approval:request', handleRequest);
    socket.on('approval:response', handleResponse);
    return () => {
      socket.off('approval:request', handleRequest);
      socket.off('approval:response', handleResponse);
    };
  }, [socket, removeApproval]);

  const respond = useCallback(async (requestId: string, decision: ApprovalDecision, message: string) => {
    setRespondingTo(requestId);
    setAnswerErrors((prev) => withoutKey(prev, requestId));
    try {
      await respondToApproval(requestId, decision, message);
      removeApproval(requestId);
      return true;
    } catch (err) {
      console.error('[usePendingApprovals] answer not accepted', err);
      setAnswerErrors((prev) => ({ ...prev, [requestId]: approvalAnswerError(err) }));
      return false;
    } finally {
      setRespondingTo(null);
    }
  }, [removeApproval]);

  return { approvals, loadError, respondingTo, answerErrors, respond };
}
